import React from 'react';
import QuickCheck from '@/components/QuickCheck';
import type { OpenActionSpec, ResolvedWarning, VariableSpec } from '@/shared/warnings/types';
// JSON artifact to resolve token metadata
import variableRegistry from '@/shared/warnings/variableRegistry.json';
// NI transformation for council tax → council rates
import { replaceCouncilTaxWithRates } from '@/shared/lib/niSchemaTransformer';

const variableSpecs = variableRegistry as VariableSpec[];

type Props = {
  warning: ResolvedWarning;
  onOpenFieldEdit?: ((schemaKey: string, fieldName: string, apiFieldName: string) => void) | undefined;
  onNotesDialogOpen?: (() => void) | undefined;
  onWarningAction?: ((action: OpenActionSpec, metadata?: { varId?: string }) => void) | undefined;
  /** When true, transforms "council tax" to "council rates" for NI users */
  isNorthernIreland?: boolean;
};

const FieldWarning: React.FC<Props> = ({ warning, onOpenFieldEdit, onNotesDialogOpen, onWarningAction, isNorthernIreland = false }) => {
  if (!warning) return null;

  const PROFILE_TOKENS_WITH_EDIT_ICON = new Set([
    'customerEmploymentStatus',
    'partnerEmploymentStatus',
    'housingTenure',
    'noOfVehiclesInHouseholds',
  ]);

  // Helper to apply NI transformation if needed
  const transformForNI = (text: string): string => {
    return isNorthernIreland ? replaceCouncilTaxWithRates(text) : text;
  };

  const segments = (warning.tokens || []).map((t) => {
    const spec = variableSpecs.find((v) => v.id === t.varId);
    const openAction = spec?.openAction;
    
    // Notes tokens (varId ends with _NOTES or label is "add a note") should use openWarningAction
    // with isNotesAction flag so handler knows to open notes dialog
    const isNotesToken = t.varId.endsWith('_NOTES') || t.label === 'add a note';
    
    // Determine the appropriate action for this token
    // Notes tokens use openWarningAction with openAction containing notes focus path
    let action: 'openWarningAction' | 'openFieldEdit';
    if (openAction) {
      action = 'openWarningAction';
    } else {
      action = 'openFieldEdit';
    }
    
    return {
      // Apply NI transformation to segment label (e.g., "council tax" → "council rates")
      text: transformForNI(t.label),
      action,
      targetSchemaFieldName: spec?.schemaFieldPath,
      targetApiFieldName: spec?.backendField,
      schemaKey: spec?.inner,
      openAction,
      varId: t.varId,
      showEditIcon: PROFILE_TOKENS_WITH_EDIT_ICON.has(t.varId),
      isNotesToken, // Flag to help handler identify notes tokens
    };
  });

  // Replace common placeholders in the message using available token data
  // Apply NI transformation to the label before inserting into message
  const firstTokenLabel = transformForNI(warning.tokens?.[0]?.label || '');
  let message = warning.resolvedText || '';
  if (message && firstTokenLabel) {
    message = message.split('{field.label}').join(firstTokenLabel);
  }
  // Also apply NI transformation to any remaining "council tax" in the message
  message = transformForNI(message);

  return (
    <QuickCheck
      message={message}
      segments={segments}
      onOpenFieldEdit={onOpenFieldEdit}
      onNotesDialogOpen={onNotesDialogOpen}
      onOpenWarningAction={onWarningAction}
    />
  );
};

export default FieldWarning;


